import { memo } from "react"
import { ChevronDown, Database, Loader2, Sparkles } from "lucide-react"
import Editor from "react-simple-code-editor"

import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { MultimodalInput } from "@/components/ui/multimodal-input"
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

type SelectionRange = { start: number; end: number; text: string }

export const SqlEditorPanel = memo(function SqlEditorPanel(props: {
  activeQueryId: string
  activeQuerySqlForUpdate: string
  activeSql: string
  setActiveSql: (value: string) => void
  activeSqlRef: React.MutableRefObject<string>
  sqlEditorRef: React.RefObject<HTMLDivElement | null>
  sqlEditorScrollRef: React.RefObject<HTMLDivElement | null>
  lastSqlSelectionRef: React.MutableRefObject<SelectionRange | null>

  skipSqlHistoryRef: React.MutableRefObject<boolean>
  sqlHistoryRef: React.MutableRefObject<Record<string, { undo: string[]; redo: string[] }>>
  pushSqlUndo: (tabId: string, previousSql: string, nextSql: string) => void

  selectedResource: string
  setSelectedResource: (value: string) => void
  resources: { id: string; name: string }[]

  // assistants
  queryAssistantOpen: boolean
  setQueryAssistantOpen: (open: boolean) => void
  queryQuestion: string
  setQueryQuestion: (value: string) => void
  queryQuestionInputRef: React.MutableRefObject<{
    focus: () => void
    setValue: (value: string) => void
    getValue: () => string
  } | null>

  selectionAssistantOpen: boolean
  setSelectionAssistantOpen: (open: boolean) => void
  selectionQuestion: string
  setSelectionQuestion: (value: string) => void
  selectionQuestionInputRef: React.MutableRefObject<{
    focus: () => void
    setValue: (value: string) => void
    getValue: () => string
  } | null>

  frozenSqlSelection: SelectionRange | null
  setFrozenSqlSelection: (value: SelectionRange | null) => void
  sqlSelection: SelectionRange | null
  setSqlSelection: (value: SelectionRange | null) => void

  selectionHighlightRects: { left: number; top: number; width: number; height: number }[]
  selectionBubble: { x: number; y: number } | null

  setSelectionHighlightRects: (value: { left: number; top: number; width: number; height: number }[]) => void
  refreshSelectionChrome: () => void
  scheduleSelectionRefresh: () => void

  highlightedSqlCacheRef: React.MutableRefObject<{ code: string; html: string }>

  autosaveAfterChange: (tabId: string, sql: string) => void
  isWidgetQuery: boolean

  // mutations
  generateQueryIsPending: boolean
  onGenerateQuery: (resourceId: string, question: string) => void

  updateQueryIsPending: boolean
  onUpdateQuery: (args: { resource_id: string; sql: string; selected_sql: string; question: string }) => void

  onRunQuery: () => void
  onUndo: () => void
  onRedo: () => void
}) {
  return (
    <div className="flex h-full overflow-hidden bg-white dark:bg-[#0F0F12]">
      <div className="w-10 shrink-0 border-r border-gray-200 bg-gray-50 text-right font-mono text-[10px] leading-[1.5] text-gray-400 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-500">
        <div className="px-1.5 py-2">
          {(props.activeSql || "-- Write your SQL query here...").split("\n").map((_, i) => (
            <div key={i} className="py-[2px]">
              {i + 1}
            </div>
          ))}
        </div>
      </div>

      <div ref={props.sqlEditorRef} className="sql-editor relative flex-1">
        <div ref={props.sqlEditorScrollRef} className="sql-editor-scroll relative h-full overflow-auto">
          {props.selectionAssistantOpen && props.selectionHighlightRects.length > 0 ? (
            <div className="pointer-events-none absolute inset-0 z-[1]" aria-hidden>
              {props.selectionHighlightRects.map((r, i) => (
                <div
                  key={i}
                  className="absolute rounded-sm bg-violet-300/35 dark:bg-violet-400/25"
                  style={{ left: r.left, top: r.top, width: r.width, height: r.height }}
                />
              ))}
            </div>
          ) : null}

          <Editor
            value={props.activeSql}
            onValueChange={(value) => {
              if (!props.skipSqlHistoryRef.current) {
                if (props.activeSqlRef.current !== value) {
                  props.pushSqlUndo(props.activeQueryId, props.activeSqlRef.current, value)
                }
              } else {
                props.skipSqlHistoryRef.current = false
              }

              props.setActiveSql(value)
              props.activeSqlRef.current = value

              if (!props.selectedResource || props.isWidgetQuery) return
              props.autosaveAfterChange(props.activeQueryId, value)
            }}
            onKeyDown={(e: React.KeyboardEvent) => {
              const mod = e.metaKey || e.ctrlKey
              if (mod && e.key === "Enter") {
                e.preventDefault()
                props.onRunQuery()
                return
              }
              if (mod && (e.key === "z" || e.key === "Z")) {
                e.preventDefault()
                if (e.shiftKey) props.onRedo()
                else props.onUndo()
                return
              }
              if (e.ctrlKey && e.altKey && (e.key === "z" || e.key === "Z")) {
                e.preventDefault()
                props.onRedo()
                return
              }
              if (mod && (e.key === "y" || e.key === "Y")) {
                e.preventDefault()
                props.onRedo()
                return
              }
              if (e.key === "/" && !e.metaKey && !e.ctrlKey && !e.altKey) {
                e.preventDefault()
                props.setQueryAssistantOpen(true)
                requestAnimationFrame(() => props.queryQuestionInputRef.current?.focus())
              }
            }}
            onMouseUp={() => props.scheduleSelectionRefresh()}
            onKeyUp={() => props.scheduleSelectionRefresh()}
            highlight={(code) => {
              const cached = props.highlightedSqlCacheRef.current
              if (cached.code === code) return cached.html
              return code
            }}
            padding={{ top: 8, right: 12, bottom: 12, left: 12 }}
            placeholder='-- Write SQL here (press "/" to open the Query Assistant)...'
            className="relative z-[2] h-full min-h-min w-full font-mono text-xs"
            style={{
              fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
              lineHeight: 1.5,
              color: "inherit",
            }}
            textareaClassName="border-0 bg-transparent outline-none focus:ring-0 caret-gray-900 dark:caret-gray-100"
            tabSize={4}
          />
        </div>

        {props.sqlSelection && props.selectionBubble ? (
          <div
            className="absolute z-20"
            style={{ left: Math.max(8, props.selectionBubble.x), top: Math.max(8, props.selectionBubble.y - 28) }}
          >
            <Popover
              open={props.selectionAssistantOpen}
              onOpenChange={(open) => {
                props.setSelectionAssistantOpen(open)
                if (!open) {
                  props.setFrozenSqlSelection(null)
                  props.setSelectionHighlightRects([])
                  requestAnimationFrame(() => props.refreshSelectionChrome())
                }
                if (open) requestAnimationFrame(() => props.selectionQuestionInputRef.current?.focus())
              }}
            >
              {props.selectionAssistantOpen ? (
                <PopoverAnchor asChild>
                  <span className="inline-block h-0 w-0 overflow-hidden p-0 opacity-0" aria-hidden />
                </PopoverAnchor>
              ) : (
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="h-7 gap-1.5 rounded-full border border-gray-200 bg-white/90 px-2 text-[11px] shadow-sm backdrop-blur hover:bg-white dark:border-gray-800 dark:bg-[#0F0F12]/90"
                    onPointerDownCapture={() => {
                      const root = props.sqlEditorRef.current
                      const textarea = root?.querySelector("textarea") as HTMLTextAreaElement | null
                      let snap: SelectionRange | null = null
                      if (textarea) {
                        const s = textarea.selectionStart ?? 0
                        const e = textarea.selectionEnd ?? 0
                        if (s !== e) snap = { start: s, end: e, text: textarea.value.slice(s, e) }
                      }
                      if (!snap) snap = props.lastSqlSelectionRef.current
                      if (snap) {
                        props.setFrozenSqlSelection(snap)
                        props.setSqlSelection(snap)
                        props.lastSqlSelectionRef.current = snap
                      }
                    }}
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    Ask AI
                  </Button>
                </PopoverTrigger>
              )}
              <PopoverContent
                align="start"
                side="top"
                sideOffset={8}
                className="w-96 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-800 dark:bg-[#0F0F12]"
                onOpenAutoFocus={(e) => e.preventDefault()}
              >
                <div className="space-y-2.5">
                  <MultimodalInput
                    placeholder="Describe the change you want (e.g. add a filter, change sort, join another table)."
                    containerClassName="rounded-xl border-gray-100 bg-gray-50/50 shadow-none dark:border-gray-800 dark:bg-transparent"
                    textAreaClassName="placeholder:text-xs placeholder:text-gray-400 dark:placeholder:text-gray-500"
                    popoverClassName="pt-2 px-2"
                    allowMentionsInEditMode
                    isSubmitting={props.updateQueryIsPending}
                    clearOnSubmit={false}
                    hideControlBarSpacer
                    value={props.selectionQuestion}
                    setValue={props.setSelectionQuestion}
                    inputRef={props.selectionQuestionInputRef}
                    selectedResourceId={props.selectedResource}
                    onSubmit={(message) => {
                      if (!message.trim() || !props.selectedResource || props.updateQueryIsPending) return
                      const sel = props.frozenSqlSelection ?? props.sqlSelection
                      if (!sel) return
                      props.onUpdateQuery({
                        resource_id: props.selectedResource,
                        sql: props.activeQuerySqlForUpdate,
                        selected_sql: sel.text,
                        question: message.trim(),
                      })
                    }}
                    isEditMode
                  />
                </div>
              </PopoverContent>
            </Popover>
          </div>
        ) : null}

        <div className="absolute bottom-2 right-2 z-30">
          <Popover
            open={props.queryAssistantOpen}
            onOpenChange={(open) => {
              props.setQueryAssistantOpen(open)
              if (open) requestAnimationFrame(() => props.queryQuestionInputRef.current?.focus())
            }}
          >
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="m-2 h-8 w-8 rounded-lg border border-gray-200 bg-white/80 backdrop-blur hover:bg-white dark:border-gray-800 dark:bg-[#0F0F12]/80"
                tooltip="Ask AI to generate query"
              >
                <Sparkles className="h-4 w-4 text-gray-700 dark:text-gray-200" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              side="top"
              sideOffset={8}
              className="w-96 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-800 dark:bg-[#0F0F12]"
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              <div className="space-y-2.5">
                <div className="flex items-center justify-between gap-2">
                  {props.generateQueryIsPending ? (
                    <span className="flex items-center gap-1 text-[10px] text-gray-500 dark:text-gray-400">
                      <Loader2 className="h-3 w-3 shrink-0 animate-spin" />
                      Generating…
                    </span>
                  ) : null}
                </div>
                <MultimodalInput
                  placeholder="Ask anything about your data and get the relevant query, use @ to mention tables or columns."
                  containerClassName="rounded-xl border-gray-100 bg-gray-50/50 shadow-none dark:border-gray-800 dark:bg-transparent"
                  textAreaClassName="placeholder:text-xs placeholder:text-gray-400 dark:placeholder:text-gray-500"
                  popoverClassName="pt-2 px-2"
                  allowMentionsInEditMode
                  isSubmitting={props.generateQueryIsPending}
                  clearOnSubmit={false}
                  hideControlBarSpacer
                  value={props.queryQuestion}
                  setValue={props.setQueryQuestion}
                  inputRef={props.queryQuestionInputRef}
                  selectedResourceId={props.selectedResource}
                  controlBarStart={
                    <div className="min-w-0 shrink-0">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 min-w-0 max-w-[13rem] justify-start gap-1.5 text-xs font-normal"
                            disabled={props.generateQueryIsPending}
                            title={
                              props.selectedResource
                                ? (props.resources.find((r) => r.id === props.selectedResource)?.name ?? "Resource")
                                : "Select resource"
                            }
                          >
                            <Database className="h-3.5 w-3.5 shrink-0 text-gray-700 dark:text-gray-200" />
                            <span className="min-w-0 flex-1 truncate text-left">
                              {props.selectedResource
                                ? (props.resources.find((r) => r.id === props.selectedResource)?.name ?? "Resource")
                                : "Select resource"}
                            </span>
                            <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="max-h-48 overflow-y-auto">
                          {props.resources.map((r) => (
                            <DropdownMenuItem
                              key={r.id}
                              className="gap-2 text-xs"
                              onClick={() => props.setSelectedResource(r.id)}
                            >
                              <Database className="h-3.5 w-3.5 shrink-0" />
                              <span className="min-w-0 flex-1 truncate">{r.name}</span>
                              {props.selectedResource === r.id ? (
                                <span className="text-[10px] text-gray-500">✓</span>
                              ) : null}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  }
                  onSubmit={(message) => {
                    if (!message.trim() || !props.selectedResource || props.generateQueryIsPending) return
                    props.onGenerateQuery(props.selectedResource, message.trim())
                  }}
                  isEditMode
                />
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  )
})
